import * as React from 'react';
import styled from 'styled-components';
import {
  IconPlayAlt,
  IconPauseAlt,
  Image,
  TooltipTrigger,
  Tooltip,
} from '@spotify-internal/encore-web';
import recordingPlaceholderImg from './placeholder-recording.png';
import { Audio } from 'components/MediaPlayback';
import Toggle from 'components/Toggle';
import gTagManager from 'libs/services/logging';
import { TooltipPositions } from 'components/DataTable';
import { useT } from '@spotify-internal/i18n-core';

type Recording = {
  artistName: string;
  audioPreview: string;
  coverart300?: string;
  coverart640?: string;
  gid: string;
  title: string;
};

type Props = {
  recording: Recording;
  size: 'S' | 'L';
  trackingEvent?: string;
  isHeaderImage?: boolean;
  tooltipPlacement?: TooltipPositions;
};

const placeholderImageForSize = (size?: string) => {
  switch (size) {
    case 'S':
      return recordingPlaceholderImg;
    case 'L':
      return recordingPlaceholderImg;
    default:
      throw new Error(`No image found for size ${size}`);
  }
};

const iconSizeForSize = (size?: string) => {
  switch (size) {
    case 'S':
      return 32;
    case 'L':
      return 48;
    default:
      throw new Error(`No play icon size found for size ${size}`);
  }
};

type LazyImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  placeholderSrc: string;
};

type ThumbnailProps = {
  isHeaderImage?: boolean;
  size: string;
} & LazyImageProps;

function thumbnailSize(props: ThumbnailProps) {
  if (props.isHeaderImage) {
    return '100%';
  }

  return props.size === 'S' ? '64px' : '93px';
}

const Thumbnail = styled(({ isHeaderImage, size, ...props }: ThumbnailProps) => (
  <Image {...props} />
))`
  width: ${thumbnailSize};
  height: ${thumbnailSize};
`;

const PreviewButtonOverlay = styled.div`
  position: absolute;
  color: #fff;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  opacity: 0;
  transition: opacity 0.25s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);

  &:hover,
  &:focus {
    opacity: 1;

    svg {
      transform: scale(1.15, 1.15);
    }
  }

  svg {
    transition: transform 0.25s ease-out;
  }
`;

const PreviewButton = styled.button`
  position: relative;
  border: none;
  padding: 0;
  background: none;
  outline: none;
  cursor: pointer;
`;

function NoPreviewTooltip(props: { placement: TooltipPositions; children: React.ReactNode }) {
  const t = useT();
  return (
    <Toggle>
      {(toggle) => (
        <TooltipTrigger
          overlay={
            toggle.isActive && (
              <Tooltip>
                {t(
                  'TOOLTIP_RECORDINGIMAGE_0',
                  'No preview available',
                  'Tooltip that shows when there is no preview available',
                )}
              </Tooltip>
            )
          }
          placement={props.placement}
          onShow={toggle.activate}
          onHide={toggle.deactivate}
        >
          {props.children}
        </TooltipTrigger>
      )}
    </Toggle>
  );
}

function RecordingImage(props: Props) {
  const t = useT();
  const { recording, size, isHeaderImage, trackingEvent, tooltipPlacement } = props;

  if (!recording.audioPreview) {
    return (
      <NoPreviewTooltip placement={tooltipPlacement || 'top'}>
        <Thumbnail
          src={recording.coverart640 || recording.coverart300 || placeholderImageForSize(size)}
          alt={t(
            'ALTTEXT_RECORDINGIMAGE_0',
            'Artwork for {title}',
            'Alt text for image for recording',
            { title: recording.title },
          )}
          isHeaderImage={isHeaderImage}
          placeholderSrc={placeholderImageForSize(size)}
          size={size}
        />
      </NoPreviewTooltip>
    );
  }

  return (
    <Audio src={recording.audioPreview} pauseOnUnmount>
      {(audio) => (
        <PreviewButton
          onClick={(e) => {
            e.stopPropagation();

            if (audio.isPlaying) {
              audio.pause();
            } else {
              audio.play();
            }

            if (trackingEvent)
              gTagManager({
                event: trackingEvent,
                recordingGid: recording.gid,
                recordingTitle: recording.title,
                recordingArtist: recording.artistName,
              });
          }}
        >
          <Thumbnail
            src={recording.coverart640 || recording.coverart300 || placeholderImageForSize(size)}
            alt={
              audio.isPlaying
                ? t(
                    'ALTTEXT_RECORDINGIMAGE_1',
                    'Pause a preview of {title}',
                    'Alt text for image for recording',
                    { title: recording.title },
                  )
                : t(
                    'ALTTEXT_RECORDINGIMAGE_2',
                    'Play a preview of {title}',
                    'Alt text for image for recording',
                    { title: recording.title },
                  )
            }
            isHeaderImage={isHeaderImage}
            placeholderSrc={placeholderImageForSize(size)}
            size={size}
          />
          <PreviewButtonOverlay tabIndex={0}>
            {audio.isPlaying ? (
              <IconPauseAlt iconSize={iconSizeForSize(size)} />
            ) : (
              <IconPlayAlt iconSize={iconSizeForSize(size)} />
            )}
          </PreviewButtonOverlay>
        </PreviewButton>
      )}
    </Audio>
  );
}

export default RecordingImage;
